import { Paper, Stack, Typography } from '@mui/material'
import { StoppageResponseDto } from '@operations/app/api'
import { EntityTable } from '@operations/components/table/EntityTable'
import { useTranslate } from '@tolgee/react'
import { EmptyIndicator, OptionsButton } from '@tom-ui/ui'
import { PATHS } from '@tom-ui/utils'
import dayjs from 'dayjs'
import { MRT_ColumnDef } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import { useNavigate } from 'react-router'
import { StoppageDate } from '../Atoms/StoppageDate'
import { StoppageEquipments } from '../Atoms/StoppageEquipments'

interface Props {
  stoppages: StoppageResponseDto[]
  vesselVisitId: number
  isVesselVisit?: boolean
  onDelete: (id: number) => void
}

export const StoppageTable = observer(
  ({ stoppages, vesselVisitId, isVesselVisit, onDelete }: Props) => {
    const { t } = useTranslate()
    const navigate = useNavigate()

    const columns = useMemo<MRT_ColumnDef<StoppageResponseDto>[]>(
      () => [
        {
          accessorKey: 'name',
          header: t('name', 'Name'),
          accessorFn: row => (
            <Typography variant='subtitle2'>{row.stoppageReason?.name}</Typography>
          ),
        },
        {
          accessorKey: 'reason',
          header: t('reason', 'Reason'),
          accessorFn: row => (
            <Typography variant='subtitle2'>{t(row.stoppageReason?.type)}</Typography>
          ),
        },
        {
          accessorKey: 'responsible',
          header: t('responsible', 'Responsible'),
          accessorFn: row => (
            <Typography variant='subtitle2'>{t(row.stoppageReason?.responsible)}</Typography>
          ),
        },
        {
          accessorKey: 'areaAffected',
          header: t('areaAffected', 'Area affected'),
          Cell: cell => (
            <StoppageEquipments
              equipments={cell.row.original.equipments}
              isVesselVisit={isVesselVisit}
            />
          ),
        },
        {
          accessorKey: 'date',
          header: t('date', 'Date'),
          sortingFn: (rowA, rowB) => dayjs(rowA.original.from).diff(rowB.original.from),
          accessorFn: ({ from, to }) => (
            <Stack direction='row' alignItems='center' spacing={4}>
              <StoppageDate date={from} />

              {to ? (
                <StoppageDate date={to} />
              ) : (
                <Typography variant='subtitle2'>{EmptyIndicator}</Typography>
              )}
            </Stack>
          ),
        },
        {
          accessorKey: 'notes',
          header: t('notes', 'Notes'),
          accessorFn: row => (
            <Typography variant='subtitle2'>{row.notes ?? EmptyIndicator}</Typography>
          ),
        },
      ],
      [t, isVesselVisit],
    )

    return (
      <Paper variant='elevation' elevation={1}>
        <EntityTable
          columns={columns}
          items={stoppages}
          isFiltering
          customizeRowActions={(row: any) => (
            <OptionsButton
              tooltip={t('moreOptions', 'More options')}
              options={[
                {
                  label: t('edit', 'Edit'),
                  onClick: () => {
                    navigate(
                      `${PATHS.visits.vesselVisits.url}/${vesselVisitId}/${PATHS.visits.vesselVisits.stoppages.url}/${row.original.id}`,
                    )
                  },
                },
                {
                  label: t('delete', 'delete'),
                  onClick: () => {
                    onDelete(row.original.id)
                  },
                },
              ]}
            />
          )}
        />
      </Paper>
    )
  },
)
